// Pre-sales for the screens: one indexPresales() (shared/presales.ts) over the synced tables, rebuilt only when one of
// them changes (or the day does) and shared by every component that asks: the run's Pre-sales tab and header chip,
// planning (planReservations()), consignment (what backers' boxes still take at a CM). Receivers get no pre-sale
// tables, so for them it's empty.
import { planToday } from "../../../shared/day.ts";
import type { SyncedTables } from "../../../shared/db.gen.ts";
import type { PlanReservation } from "../../../shared/planning.ts";
import { indexPresales, type PresaleIndex, planReservations, presaleProducts } from "../../../shared/presales.ts";
import { store, useRows } from "./sync.ts";

const TABLES = [
  "presale",
  "presale_line",
  "production_run_hold",
  "presale_income",
  "presale_shipment",
  "presale_shipment_line",
  "item",
  "production_run",
  "production_run_line",
  "po",
  "po_line",
  "location",
] as const satisfies readonly (keyof SyncedTables)[];
type Rows = { [K in (typeof TABLES)[number]]: SyncedTables[K][] };

let last: { inputs: unknown[]; ix: PresaleIndex; reservations: PlanReservation[] | null; products: string[] | null } | null = null;
// The last reservations handed out: kept while they say the same, so a PO line or item edit that doesn't move them
// doesn't make the plan start over (usePlan() plans everything again when they change).
let given: { key: string; list: PlanReservation[] } | null = null;
const stable = (list: PlanReservation[]) => {
  const key = JSON.stringify(list);
  if (given?.key !== key) given = { key, list };
  return given.list;
};
let givenProducts: { key: string; list: string[] } | null = null;
const stableProducts = (list: string[]) => {
  const key = list.join(",");
  if (givenProducts?.key !== key) givenProducts = { key, list };
  return givenProducts.list;
};

function latest(rows: Rows, today: string) {
  const inputs = [...TABLES.map((t) => rows[t]), today];
  if (!last || last.inputs.length !== inputs.length || last.inputs.some((x, i) => x !== inputs[i])) {
    last = {
      inputs,
      ix: indexPresales(
        {
          presales: rows.presale,
          lines: rows.presale_line,
          holds: rows.production_run_hold,
          income: rows.presale_income,
          shipments: rows.presale_shipment,
          shipmentLines: rows.presale_shipment_line,
        },
        {
          today,
          items: rows.item,
          runs: rows.production_run,
          runLines: rows.production_run_line,
          pos: rows.po,
          poLines: rows.po_line,
          locations: rows.location,
        },
      ),
      reservations: null,
      products: null,
    };
  }
  return last;
}

function useLatest() {
  const rows: Rows = {
    presale: useRows("presale"),
    presale_line: useRows("presale_line"),
    production_run_hold: useRows("production_run_hold"),
    presale_income: useRows("presale_income"),
    presale_shipment: useRows("presale_shipment"),
    presale_shipment_line: useRows("presale_shipment_line"),
    item: useRows("item"),
    production_run: useRows("production_run"),
    production_run_line: useRows("production_run_line"),
    po: useRows("po"),
    po_line: useRows("po_line"),
    location: useRows("location"),
  };
  return latest(rows, planToday());
}

export function usePresaleIndex(): PresaleIndex {
  return useLatest().ix;
}

/** What planning must not count as for sale, worked out once per change of the inputs. */
export function useReservations(): PlanReservation[] {
  const l = useLatest();
  l.reservations ??= stable(planReservations(l.ix));
  return l.reservations;
}

/** Shopify products a pre-sale sells: planned though their title says Kickstarter (kept while they say the same). */
export function usePresaleProducts(): string[] {
  const l = useLatest();
  l.products ??= stableProducts(presaleProducts(l.ix));
  return l.products;
}

/** The same, straight from the store, outside React (the Check against the plan runner). */
export function presaleProductsNow(): string[] {
  const rows = Object.fromEntries(TABLES.map((t) => [t, store.rows(t)])) as unknown as Rows;
  const l = latest(rows, planToday());
  l.products ??= stableProducts(presaleProducts(l.ix));
  return l.products;
}

/** The same, straight from the store, outside React (the Check against the plan runner). */
export function reservationsNow(): PlanReservation[] {
  const rows = Object.fromEntries(TABLES.map((t) => [t, store.rows(t)])) as unknown as Rows;
  const l = latest(rows, planToday());
  l.reservations ??= stable(planReservations(l.ix));
  return l.reservations;
}
